import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  IncidentCategory,
  IncidentFilters,
  IncidentSeverity,
  IncidentType,
} from '../../../api/types';
import { acknowledgeIncident, fetchIncident, fetchIncidents } from '../api/incidentsApi';

// AC-19: пагинация обязательна — выдача никогда не возвращает весь набор.
// Значение совпадает с дефолтом backend'а (IncidentQuery.limit).
export const INCIDENTS_PAGE_SIZE = 50;

export const INCIDENTS_QUERY_KEY = ['admin', 'incidents'] as const;

/** AC-18: фильтры читаются из query-параметров URL — ссылка шарится, как /logs. */
function readFilters(searchParams: URLSearchParams): IncidentFilters {
  const acknowledged = searchParams.get('acknowledged');
  const offset = searchParams.get('offset');
  return {
    date_from: searchParams.get('date_from') ?? undefined,
    date_to: searchParams.get('date_to') ?? undefined,
    severity: (searchParams.get('severity') as IncidentSeverity | null) ?? undefined,
    category: (searchParams.get('category') as IncidentCategory | null) ?? undefined,
    incident_type: (searchParams.get('incident_type') as IncidentType | null) ?? undefined,
    acknowledged:
      acknowledged === null || acknowledged === '' ? undefined : acknowledged === 'true',
    limit: INCIDENTS_PAGE_SIZE,
    offset: offset ? Number(offset) : 0,
  };
}

// PARA-129: состояние ленты — фильтры ↔ URL, пагинация, загрузка, acknowledge.
export function useIncidents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const query = useQuery({
    queryKey: [...INCIDENTS_QUERY_KEY, filters] as const,
    queryFn: () => fetchIncidents(filters),
  });

  const setFilter = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      // Смена фильтра всегда возвращает на первую страницу: иначе offset от
      // прошлой выборки указал бы за пределы новой (пустая таблица без причины).
      next.delete('offset');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const setOffset = useCallback(
    (offset: number): void => {
      const next = new URLSearchParams(searchParams);
      if (offset > 0) {
        next.set('offset', String(offset));
      } else {
        next.delete('offset');
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  const acknowledge = useMutation({
    mutationFn: (incidentId: number) => acknowledgeIncident(incidentId),
    // AC-17: строка обновляется без перезагрузки страницы.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INCIDENTS_QUERY_KEY });
    },
  });

  const offset = filters.offset ?? 0;
  const total = query.data?.total ?? 0;

  return {
    filters,
    items: query.data?.items ?? [],
    total,
    offset,
    pageSize: INCIDENTS_PAGE_SIZE,
    hasPrevious: offset > 0,
    hasNext: offset + INCIDENTS_PAGE_SIZE < total,
    isLoading: query.isLoading,
    error: query.error,
    setFilter,
    setOffset,
    acknowledge,
  };
}

// AC-16: полная карточка грузится отдельным запросом — в списке длинных
// технических полей (stack trace) нет, тянуть их на всю страницу незачем.
export function useIncident(incidentId: number | null) {
  return useQuery({
    queryKey: [...INCIDENTS_QUERY_KEY, 'detail', incidentId] as const,
    queryFn: () => fetchIncident(incidentId as number),
    enabled: incidentId !== null,
  });
}
